import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';

const NAV_KEYS = [
  { key: 'models', href: '/models' },
  { key: 'buildYourBoat', href: '/configurator' },
  { key: 'about', href: '/about' },
  { key: 'contact', href: '/contact' },
];

const SEARCH_INDEX = [
  { label: 'Manta Ray 420', sublabel: 'Center Console · 4.2 m · 50 HP', href: '/models/mantaray420' },
  { label: 'STIA 580', sublabel: 'Bowrider · 5.8 m · 140 HP', href: '/models/stia580' },
  { label: 'Real 500', sublabel: 'Deck Boat · 5.0 m · 100 HP', href: '/models/real500' },
  { label: 'Build Your Boat', sublabel: 'Configure STIA 580 online', href: '/configurator' },
  { label: 'Our Models', sublabel: 'Explore all Unimar boats', href: '/models' },
  { label: 'About Us', sublabel: 'Our story and manufacturing', href: '/about' },
  { label: 'Contact', sublabel: 'Get in touch with Unimar', href: '/contact' },
  { label: 'Book a Showcase', sublabel: 'Schedule a private viewing', href: '/book-showcase' },
  { label: 'Gallery', sublabel: 'Browse all boat photos', href: '/gallery' },
];

function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const setLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    setLangOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    if (langOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langOpen]);

  return (
    <>
    <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
      <div className="header__inner">
        {/* Logo */}
        <a href="/" className="header__logo">
          <img src="/logo-unimar.webp" alt="UniMar" className="header__logo-img" />
        </a>

        {/* Desktop Navigation */}
        <nav className="header__nav">
          {NAV_KEYS.map((item) => (
            <a key={item.key} href={item.href} className="header__nav-link">
              {t(`nav.${item.key}`)}
            </a>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="header__actions">
          {/* Phone — desktop only */}
          <a href="tel:+38976422242" className="header__phone">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.5 2.4.8 3.7.8.6 0 1 .4 1 1v3.5c0 .6-.4 1-1 1C10.7 21.3 2.7 13.3 2.7 3.9c0-.6.4-1 1-1H7.2c.6 0 1 .4 1 1 0 1.3.3 2.5.8 3.7.1.4 0 .8-.2 1.1l-2.2 2.1z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
            <span>+389 76 42 22 42</span>
          </a>

          {/* Search Button */}
          <button
            className="header__search-btn"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-label="Search"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="9.5"
                cy="9.5"
                r="6.5"
                stroke="#14234C"
                strokeWidth="2"
              />
              <line
                x1="14.5"
                y1="14.5"
                x2="20"
                y2="20"
                stroke="#14234C"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Language Dropdown — desktop only */}
          <div className={`header__lang-dropdown${langOpen ? ' header__lang-dropdown--open' : ''} header__lang-dropdown--desktop`} ref={langRef}>
            <button
              className="header__lang-trigger"
              onClick={() => setLangOpen(!langOpen)}
              aria-label="Select language"
            >
              <span className="header__lang-trigger-text">{t('nav.language')}</span>
              <svg className="header__lang-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="#14234C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {langOpen && (
              <div className="header__lang-menu">
                <button
                  className={`header__lang-item${i18n.language === 'en' ? ' header__lang-item--active' : ''}`}
                  onClick={() => setLang('en')}
                >
                  <span className="header__lang-flag">🇬🇧</span>
                  <span className="header__lang-label">English</span>
                </button>
                <button
                  className={`header__lang-item${i18n.language === 'mk' ? ' header__lang-item--active' : ''}`}
                  onClick={() => setLang('mk')}
                >
                  <span className="header__lang-flag">🇲🇰</span>
                  <span className="header__lang-label">Македонски</span>
                </button>
              </div>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`header__hamburger ${mobileMenuOpen ? 'header__hamburger--open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Search Overlay — only mounted when open, no hidden layer blocking clicks */}
      {searchOpen && (
        <div className="header__search-overlay header__search-overlay--open">
          <div className="header__search-container">
            <input
              type="text"
              className="header__search-input"
              placeholder={t('nav.searchPlaceholder')}
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="header__search-close"
              onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
              aria-label="Close search"
            >
              ✕
            </button>
          </div>
          {searchQuery.trim() && (() => {
            const q = searchQuery.toLowerCase();
            const results = SEARCH_INDEX.filter(
              (item) => item.label.toLowerCase().includes(q) || item.sublabel.toLowerCase().includes(q)
            );
            return (
              <div className="header__search-results">
                {results.length > 0 ? results.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="header__search-result"
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  >
                    <span className="header__search-result-label">{item.label}</span>
                    <span className="header__search-result-sub">{item.sublabel}</span>
                  </a>
                )) : (
                  <div className="header__search-empty">{t('nav.noResults')} "{searchQuery}"</div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Mobile Menu — rendered via portal to document.body to avoid
          any z-index / stacking-context / backdrop-filter containment
          issues with the position:fixed header on iOS Chrome */}
      {mobileMenuOpen && createPortal(
        <div className="header__mobile-menu header__mobile-menu--open">
          <nav className="header__mobile-nav">
            {NAV_KEYS.map((item, i) => (
              <a
                key={item.key}
                href={item.href}
                className="header__mobile-link"
                style={{ animationDelay: `${i * 0.06}s` }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <a
              href="tel:+38976422242"
              className="header__mobile-phone"
              style={{ animationDelay: `${NAV_KEYS.length * 0.06}s` }}
              onClick={() => setMobileMenuOpen(false)}
            >
              +389 76 42 22 42
            </a>
            <div className="header__mobile-lang">
              <button
                className={`header__mobile-lang-btn${i18n.language === 'en' ? ' header__mobile-lang-btn--active' : ''}`}
                onClick={() => { i18n.changeLanguage('en'); localStorage.setItem('lang', 'en'); setMobileMenuOpen(false); }}
              >
                <span>🇬🇧</span>
              </button>
              <button
                className={`header__mobile-lang-btn${i18n.language === 'mk' ? ' header__mobile-lang-btn--active' : ''}`}
                onClick={() => { i18n.changeLanguage('mk'); localStorage.setItem('lang', 'mk'); setMobileMenuOpen(false); }}
              >
                <span>🇲🇰</span>
              </button>
            </div>
          </nav>
        </div>,
        document.body
      )}
    </header>
    </>
  );
}

export default Header;
